import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import axios from 'axios';
import { Row, Col, Card, Form, Table, Button, InputGroup, Spinner } from 'react-bootstrap';
import ClientSearchModal from './ClientSearchModal';
import SellerSearchModal from './SellerSearchModal';
import QuoteDetailModal from './QuoteDetailModal';
import SendEmailModal from './SendEmailModal';
import { downloadQuotePDF } from '../utils/pdfGenerator';
import { sendQuoteViaWhatsApp } from '../utils/whatsappHelper';

const QuoteViewer = ({ user }) => {
    // --- ESTADOS DE DATOS ---
    const [comprobantes, setComprobantes] = useState([]);
    const [selectedComprobanteIndex, setSelectedComprobanteIndex] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [showClientModal, setShowClientModal] = useState(false);
    const [showSellerModal, setShowSellerModal] = useState(false);
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [selectedQuoteForEmail, setSelectedQuoteForEmail] = useState(null);
    const [dbClients, setDbClients] = useState([]);
    const [dbSellers, setDbSellers] = useState([]);

    // --- ESTADOS DE BÚSQUEDA Y FILTROS ---
    const [searchType, setSearchType] = useState('Clientes');
    const [entityId, setEntityId] = useState('');
    const [entityName, setEntityName] = useState('');
    const [entityDetails, setEntityDetails] = useState('');
    
    // Fechas dinámicas: SF_DESDE = SGES000.F_INCON y SF_HASTA = día de la fecha
    const getTodayDate = () => new Date().toISOString().split('T')[0];
    const getInitialFromDate = () => {
        if (user?.empresa_activa?.f_incon) {
            return user.empresa_activa.f_incon;
        }
        return '2025-07-01'; // Fallback estándar
    };

    const [fromDate, setFromDate] = useState(getInitialFromDate());
    const [toDate, setToDate] = useState(getTodayDate());
    
    // Tipo de comprobante a consultar (ALL, PRE, NPT)
    // Tipo de comprobante a consultar (ALL, PRE, NPT)
    const [documentType, setDocumentType] = useState('ALL');
    // Filtro de estado (ALL = Todos, CONFIRMADOS = Confirmados, BORRADORES = Borradores)

    const navigate = useNavigate();

    // Estado para el modal de detalle / previsualización de presupuesto
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedQuoteDetail, setSelectedQuoteDetail] = useState(null);

    const activeCompanyId = user?.empresa_activa?.c_nuges || user?.empresa_activa?.id;

    // Cargar parámetros de empresa (f_incon), clientes y vendedores de la DB
    useEffect(() => {
        const today = getTodayDate();
        axios.get('http://127.0.0.1:8000/api/empresa/parametros/')
            .then(res => {
                const sgesDesde = res.data?.f_incon;
                if (sgesDesde) {
                    setFromDate(sgesDesde);
                    setToDate(today);
                    fetchPresupuestos({ fromDate: sgesDesde, toDate: today, searchType: 'Clientes', entityId: '', documentType: 'ALL' });
                } else {
                    fetchPresupuestos({ toDate: today, searchType: 'Clientes', entityId: '', documentType: 'ALL' });
                }
            })
            .catch(err => {
                console.error("Error obteniendo parámetros de empresa para visor:", err);
                fetchPresupuestos({ toDate: today, searchType: 'Clientes', entityId: '', documentType: 'ALL' });
            });

        axios.get('http://127.0.0.1:8000/api/clientes/')
            .then(res => setDbClients(res.data))
            .catch(err => console.error("Error obteniendo clientes para visor:", err));

        axios.get('http://127.0.0.1:8000/api/vendedores/')
            .then(res => setDbSellers(res.data))
            .catch(err => console.error("Error obteniendo vendedores para visor:", err));
    }, [activeCompanyId]);

    // Función principal para obtener comprobantes de la API
    const fetchPresupuestos = (overrides = {}) => {
        setIsLoading(true);
        const activeSearchType = overrides.searchType !== undefined ? overrides.searchType : searchType;
        const activeEntityId = overrides.entityId !== undefined ? overrides.entityId : entityId;
        const activeFromDate = overrides.fromDate !== undefined ? overrides.fromDate : fromDate;
        const activeToDate = overrides.toDate !== undefined ? overrides.toDate : toDate;
        const activeDocumentType = overrides.documentType !== undefined ? overrides.documentType : documentType;
        const activeStatusFilter = overrides.statusFilter !== undefined ? overrides.statusFilter : 'ALL';

        const params = {
            f_desde: activeFromDate,
            f_hasta: activeToDate,
            from_date: activeFromDate,
            to_date: activeToDate,
            search_type: activeSearchType,
            entity_id: activeEntityId,
            estado: activeStatusFilter
        };

        if (activeDocumentType && activeDocumentType !== 'ALL') {
            params.c_compr = activeDocumentType;
        }

        if (activeEntityId && String(activeEntityId).trim()) {
            if (activeSearchType === 'Vendedores') {
                params.c_vende = String(activeEntityId).trim();
            } else {
                params.c_clien = String(activeEntityId).trim();
            }
        }

        axios.get('http://127.0.0.1:8000/api/presupuestos/', { params })
            .then(res => {
                const data = Array.isArray(res.data) ? res.data : (res.data.results || []);
                const mapped = data.map(item => {
                    const totalVal = parseFloat(item.i_total) || 0;
                    const comprType = item.c_compr ? item.c_compr.trim() : 'PRE';
                    return {
                        ...item,
                        id: (item.is_borrador || item.c_estado === 'B' || comprType === 'BOR') ? `BOR-${item.borrador_id || item.id || item.n_presu}` : (item.id || `${item.t_presu}-${item.n_presu}`),
                        is_borrador: Boolean(item.is_borrador || item.c_estado === 'B' || comprType === 'BOR'),
                        c_estcom: (item.is_borrador || item.c_estado === 'B' || comprType === 'BOR') ? 'Borrador' : (item.c_estado === 'F' ? 'Facturada' : (item.c_estado === 'A' ? 'Anulado' : 'Ingresada')),
                        i_total: totalVal,
                        f_compr: item.f_presu || item.f_compr || item.f_ingre,
                        h_compr: item.h_ingre || item.h_compr || item.h_entre || (item.h_updat ? item.h_updat.substring(0, 5) : '') || '',
                        c_usuar: item.c_usuar || 'SYS',
                        n_presu_label: `${comprType} ${String(item.n_presu).padStart(8, '0')}`
                    };
                });
                setComprobantes(mapped);
                setSelectedComprobanteIndex(0);
            })
            .catch(err => {
                console.error("Error al consultar presupuestos:", err);
                setComprobantes([]);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    // Selección desde el modal de Clientes
    const handleSelectClient = (client) => {
        setSearchType('Clientes');
        const code = String(client.c_clien || client.id || '');
        setEntityId(code);
        setEntityName(client.d_razon || client.name || '');
        setEntityDetails(client.n_cuit ? `CUIT: ${client.n_cuit}` : (client.d_domic || ''));
        setShowClientModal(false);
        fetchPresupuestos({ searchType: 'Clientes', entityId: code });
    };

    // Selección desde el modal de Vendedores
    const handleSelectSeller = (seller) => {
        setSearchType('Vendedores');
        const code = String(seller.c_vende || seller.id || '');
        setEntityId(code);
        const nom = seller.d_nombr || seller.nombre || '';
        const ape = seller.d_apell || seller.apellido || '';
        setEntityName(`${ape} ${nom}`.trim() || `Vendedor ${code}`);
        setEntityDetails(seller.d_email || seller.n_telef || 'Vendedor');
        setShowSellerModal(false);
        fetchPresupuestos({ searchType: 'Vendedores', entityId: code });
    };

    // Redirigir al cotizador para editar un borrador
    const handleEditDraft = (comp) => {
        const bId = comp.borrador_id || comp.n_presu || comp.id;
        navigate(`/quoter?borrador=${bId}`, { state: { draftQuote: comp } });
    };

    // Eliminar un borrador (con confirmación APB)
    const handleDeleteDraft = async (comp) => {
        const bId = comp.borrador_id || comp.n_presu;
        const result = await Swal.fire({
            title: '¿Eliminar borrador?',
            text: `¿Estás seguro que deseas eliminar el borrador ${comp.n_presu_label || bId}? Esta acción no se puede deshacer.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        });
        if (result.isConfirmed) {
            try {
                await axios.delete(`http://127.0.0.1:8000/api/presupuestos/borradores/${bId}/`);
                Swal.fire({
                    title: 'Eliminado',
                    text: 'El borrador ha sido eliminado correctamente.',
                    icon: 'success',
                    timer: 1800,
                    showConfirmButton: false
                });
                fetchPresupuestos();
            } catch (err) {
                console.error("Error al eliminar borrador:", err);
                Swal.fire({
                    title: 'Error',
                    text: 'No se pudo eliminar el borrador.',
                    icon: 'error',
                    confirmButtonText: 'Aceptar'
                });
            }
        }
    };

    // Apertura del modal de detalle
    const handleOpenDetailModal = (comp) => {
        setSelectedQuoteDetail(comp);
        setShowDetailModal(true);
    };

    // Apertura del modal de envío de email
    const handleOpenEmailModal = (comp) => {
        setSelectedQuoteForEmail(comp);
        setShowEmailModal(true);
    };

    // Al cambiar la entidad o tipear el código, precargar datos automáticamente
    const handleEntityCodeChange = (code) => {
        setEntityId(code);
        const cleanCode = String(code).trim();
        if (!cleanCode) {
            setEntityName('');
            setEntityDetails('');
            return;
        }

        if (searchType === 'Clientes') {
            const found = dbClients.find(c => String(c.c_clien || c.id || '').trim() === cleanCode);
            if (found) {
                setEntityName(found.d_razon || found.name || '');
                setEntityDetails(found.n_cuit ? `CUIT: ${found.n_cuit}` : (found.d_domic || ''));
            } else {
                setEntityName('');
                setEntityDetails('');
            }
        } else {
            const found = dbSellers.find(s => String(s.c_vende || s.id || '').trim() === cleanCode);
            if (found) {
                const nom = found.d_nombr || found.nombre || '';
                const ape = found.d_apell || found.apellido || '';
                setEntityName(`${ape} ${nom}`.trim() || `Vendedor ${cleanCode}`);
                setEntityDetails(found.d_email || found.n_telef || 'Vendedor');
            } else {
                setEntityName('');
                setEntityDetails('');
            }
        }
    };

    const handleTypeChange = (e) => {
        const newType = e.target.value;
        setSearchType(newType);
        setEntityId('');
        setEntityName('');
        setEntityDetails('');
        fetchPresupuestos({ searchType: newType, entityId: '' });
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-AR', {
            style: 'currency',
            currency: 'ARS',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount || 0);
    };

    // Formato de fecha para la grilla DD/MM/YY
    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const parts = dateStr.split('-');
        if (parts.length === 3) {
            const yy = parts[0].slice(-2);
            return `${parts[2]}/${parts[1]}/${yy}`;
        }
        return dateStr;
    };

    const getStatusBadge = (status) => {
        if (!status) return null;
        if (status.includes('Borrador')) {
            return <span className="badge text-dark bg-warning border border-warning-subtle px-2 py-1 fw-bold"><i className="bi bi-pencil-square me-1"></i>{status}</span>;
        }
        if (status.includes('Parcial') || status.includes('Facturar')) {
            return <span className="badge text-dark bg-warning-subtle border border-warning px-2 py-1">{status}</span>;
        } else if (status.includes('Ingresada')) {
            return <span className="badge text-primary bg-primary-subtle border border-primary px-2 py-1">{status}</span>;
        } else if (status.includes('Facturada')) {
            return <span className="badge text-success bg-success-subtle border border-success px-2 py-1">{status}</span>;
        } else if (status.includes('Anulado')) {
            return <span className="badge text-danger bg-danger-subtle border border-danger px-2 py-1">{status}</span>;
        }
        return <span className="badge text-secondary bg-secondary-subtle border border-secondary px-2 py-1">{status}</span>;
    };

    return (
        <div className="container-fluid px-4 py-3">
            {/* TITULO Y BREADCRUMB EN LA MISMA LINEA DE ESTILO DEL COTIZADOR */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <h4 className="fw-bold text-dark mb-1">
                        <i className="bi bi-file-earmark-text text-primary me-2"></i>
                        Consulta de Presupuestos
                    </h4>
                    <p className="text-muted small mb-0">
                        Remitos / Presupuestos / Facturas Proforma Emitidos a Clientes
                    </p>
                </div>
                <div className="d-flex align-items-center">
                    <span className="badge bg-secondary-subtle text-secondary border border-secondary py-2 px-3">
                        <i className="bi bi-building me-1"></i> {user?.empresa_activa?.nombre || 'SISGECOM'}
                    </span>
                </div>
            </div>

            {/* SECCION DE FILTROS Y BUSQUEDA (Estilo Identico al Cotizador) */}
            <div className="card shadow-sm border-0 mb-4">
                <div className="card-body p-3">
                    <Row className="g-2 mb-2">
                        {/* Tipo de Búsqueda (Clientes / Vendedores) */}
                        <Col md={3} lg={2}>
                            <InputGroup size="sm" className="shadow-sm">
                                <InputGroup.Text className="fw-bold bg-light flex-shrink-0" style={{ width: '80px' }}>
                                    Búsqueda
                                </InputGroup.Text>
                                <Form.Select 
                                    value={searchType} 
                                    onChange={handleTypeChange}
                                    className="fw-semibold"
                                >
                                    <option value="Clientes">Clientes</option>
                                    <option value="Vendedores">Vendedores</option>
                                </Form.Select>
                            </InputGroup>
                        </Col>

                        {/* Campo Código + Botón ? + Nombre Entidad + Teléfono/Detalles */}
                        <Col md={9} lg={10}>
                            <InputGroup size="sm" className="shadow-sm">
                                <Form.Control 
                                    type="text" 
                                    value={entityId} 
                                    onChange={(e) => handleEntityCodeChange(e.target.value)}
                                    onKeyDown={(e) => { 
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            fetchPresupuestos({ entityId: e.target.value }); 
                                        }
                                    }}
                                    onBlur={(e) => {
                                        fetchPresupuestos({ entityId: e.target.value });
                                    }}
                                    style={{ maxWidth: '85px' }}
                                    className="text-center fw-bold bg-white"
                                    placeholder="Código"
                                />
                                <Button 
                                    variant="outline-primary" 
                                    size="sm"
                                    onClick={() => {
                                        if (searchType === 'Clientes') {
                                            setShowClientModal(true);
                                        } else {
                                            setShowSellerModal(true);
                                        }
                                    }}
                                    title={searchType === 'Clientes' ? 'Buscar cliente' : 'Buscar vendedor'}
                                >
                                    <i className="bi bi-search"></i>
                                </Button>
                                {entityId && (
                                    <Button 
                                        variant="outline-secondary" 
                                        size="sm"
                                        onClick={() => {
                                            setEntityId('');
                                            setEntityName('');
                                            setEntityDetails('');
                                            fetchPresupuestos({ entityId: '' });
                                        }}
                                        title="Limpiar filtro de entidad (ver todos)"
                                    >
                                        <i className="bi bi-x-lg"></i>
                                    </Button>
                                )}
                                <Form.Control 
                                    type="text" 
                                    value={entityName || '(Todos los registros)'} 
                                    readOnly 
                                    className={`fw-bold ${entityName ? 'bg-light text-dark' : 'bg-light text-muted fst-italic'}`}
                                    style={{ flex: 2 }}
                                />
                                <Form.Control 
                                    type="text" 
                                    value={entityDetails} 
                                    readOnly 
                                    className="bg-light text-muted d-none d-md-block"
                                    style={{ flex: 1.5 }}
                                />
                            </InputGroup>
                        </Col>
                    </Row>

                    <Row className="g-2 align-items-center">
                        {/* Tipo de Consulta */}
                        <Col md={3} lg={3}>
                            <InputGroup size="sm" className="shadow-sm">
                                <InputGroup.Text className="fw-bold bg-light flex-shrink-0 px-2">
                                    Consulta de
                                </InputGroup.Text>
                                <Form.Select 
                                    value={documentType} 
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setDocumentType(val);
                                        fetchPresupuestos({ documentType: val });
                                    }}
                                    className="fw-bold text-primary"
                                >
                                    <option value="ALL">Todos los Comprobantes</option>
                                    <option value="PRE">Presupuestos (PRE)</option>
                                    <option value="BOR">Borradores (BOR)</option>
                                    <option value="NPT">Pedidos de la Tienda (NPT)</option>
                                </Form.Select>
                            </InputGroup>
                        </Col>



                        {/* Rango de Fechas (OBLIGATORIO) */}
                        <Col md={3} lg={3}>
                            <InputGroup size="sm" className="shadow-sm">
                                <InputGroup.Text className="fw-bold bg-light flex-shrink-0" style={{ width: '75px' }}>
                                    Desde el
                                </InputGroup.Text>
                                <Form.Control 
                                    type="date" 
                                    value={fromDate} 
                                    onChange={(e) => setFromDate(e.target.value)}
                                    required
                                    className="fw-semibold"
                                />
                            </InputGroup>
                        </Col>

                        <Col md={3} lg={3}>
                            <InputGroup size="sm" className="shadow-sm">
                                <InputGroup.Text className="fw-bold bg-light flex-shrink-0" style={{ width: '75px' }}>
                                    Hasta el
                                </InputGroup.Text>
                                <Form.Control 
                                    type="date" 
                                    value={toDate} 
                                    onChange={(e) => setToDate(e.target.value)}
                                    required
                                    className="fw-semibold"
                                />
                            </InputGroup>
                        </Col>

                        {/* Botón Filtrar */}
                        <Col md={3} lg={3} className="text-end">

                            <Button 
                                variant="primary" 
                                size="sm" 
                                className="w-100 fw-bold shadow-sm d-flex align-items-center justify-content-center"
                                style={{ height: '31px' }}
                                onClick={() => fetchPresupuestos()}
                                disabled={isLoading}
                            >
                                {isLoading ? <Spinner animation="border" size="sm" /> : <><i className="bi bi-search me-1"></i> Filtrar</>}
                            </Button>
                        </Col>
                    </Row>
                </div>
            </div>

            {/* TABLA PRINCIPAL: DETALLE DE COMPROBANTES (PANTALLA COMPLETA) */}
            <Card className="shadow-sm border-0">
                <Card.Header className="bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
                    <h6 className="fw-bold text-dark mb-0">
                        <i className="bi bi-list-columns-reverse text-primary me-2"></i>
                        Detalle de Comprobantes
                    </h6>
                    <span className="badge bg-primary-subtle text-primary border border-primary px-3 py-2">
                        {comprobantes.length} Comprobante(s) encontrado(s)
                    </span>
                </Card.Header>
                <Card.Body className="p-0">
                    <div className="table-responsive" style={{ minHeight: '400px', maxHeight: '550px' }}>
                        <Table hover size="sm" className="mb-0 align-middle">
                            <thead className="table-light sticky-top">
                                <tr>
                                    <th className="py-2 px-3">Nº de Presupuesto</th>
                                    <th className="py-2">Cliente</th>
                                    <th className="py-2 text-center">Emisión</th>
                                    <th className="py-2 text-center">Usuario</th>
                                    <th className="py-2 text-center">Hora</th>
                                    <th className="py-2">Estado</th>
                                    <th className="py-2 text-end">Total</th>
                                    <th className="py-2 px-3">Observaciones</th>
                                    <th className="py-2 text-center" style={{ width: '155px' }}>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {comprobantes.map((comp, idx) => {
                                    const isSelected = idx === selectedComprobanteIndex;
                                    return (
                                        <tr 
                                            key={comp.id || idx}
                                            onClick={() => {
                                                setSelectedComprobanteIndex(idx);
                                                if (comp.is_borrador) {
                                                    handleEditDraft(comp);
                                                }
                                            }}
                                            onDoubleClick={() => comp.is_borrador ? handleEditDraft(comp) : handleOpenDetailModal(comp)}

                                            className={isSelected ? 'table-primary fw-semibold' : ''}
                                            style={{ 
                                                cursor: 'pointer',
                                                backgroundColor: isSelected ? undefined : (idx % 2 === 0 ? '#ffffff' : '#fcfbf7')
                                            }}
                                        >
                                            <td className="py-2 px-3 fw-bold text-primary">{comp.n_presu_label || comp.n_presu}</td>
                                            <td className="py-2 fw-semibold text-dark" style={{ maxWidth: '200px' }}>
                                                <span className="text-truncate d-block" title={comp.d_razon}>
                                                    {comp.d_razon || 'Cliente General'}
                                                </span>
                                            </td>
                                            <td className="py-2 text-center fw-semibold">{formatDate(comp.f_compr)}</td>
                                            <td className="py-2 text-center text-secondary fw-semibold">{comp.c_usuar}</td>
                                            <td className="py-2 text-center text-muted">{comp.h_compr}</td>
                                            <td className="py-2">{getStatusBadge(comp.c_estcom)}</td>
                                            <td className="py-2 text-end fw-bold text-dark">{formatCurrency(comp.i_total)}</td>
                                            <td className="py-2 px-3 text-secondary" style={{ maxWidth: '250px' }}>
                                                <span className="text-truncate d-block" title={comp.d_obser || '-'}>
                                                    {comp.d_obser || '-'}
                                                </span>
                                            </td>
                                            <td className="py-2 text-center">
                                                <div className="d-flex justify-content-center gap-1">
                                                    {comp.is_borrador && (
                                                        <>
                                                            <Button
                                                                variant="outline-primary"
                                                                size="sm"
                                                                className="rounded-circle d-inline-flex align-items-center justify-content-center shadow-sm"
                                                                style={{ width: '30px', height: '30px', padding: 0 }}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleOpenDetailModal(comp);
                                                                }}
                                                                title="Ver detalle del borrador"
                                                            >
                                                                <i className="bi bi-eye"></i>
                                                            </Button>
                                                            <Button
                                                                variant="warning"
                                                                size="sm"
                                                                className="rounded-circle d-inline-flex align-items-center justify-content-center shadow-sm text-dark"
                                                                style={{ width: '30px', height: '30px', padding: 0 }}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleEditDraft(comp);
                                                                }}
                                                                title="Continuar editando borrador en el Cotizador"
                                                            >
                                                                <i className="bi bi-pencil-square"></i>
                                                            </Button>
                                                            <Button
                                                                variant="outline-danger"
                                                                size="sm"
                                                                className="rounded-circle d-inline-flex align-items-center justify-content-center shadow-sm"
                                                                style={{ width: '30px', height: '30px', padding: 0 }}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleDeleteDraft(comp);
                                                                }}
                                                                title="Eliminar borrador"
                                                            >
                                                                <i className="bi bi-trash"></i>
                                                            </Button>
                                                        </>
                                                    )}
                                                </div>
                                                <div className={comp.is_borrador ? 'd-none' : 'd-flex justify-content-center gap-1'}>
                                                    <Button 
                                                        variant="outline-primary" 
                                                        size="sm"
                                                        className="rounded-circle d-inline-flex align-items-center justify-content-center shadow-sm"
                                                        style={{ width: '30px', height: '30px', padding: 0 }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleOpenDetailModal(comp);
                                                        }}
                                                        title="Ver detalle del presupuesto"
                                                    >
                                                        <i className="bi bi-eye"></i>
                                                    </Button>
                                                    {user?.l_enwhat !== false && (
                                                        <Button 
                                                            variant="outline-success" 
                                                            size="sm"
                                                            className="rounded-circle d-inline-flex align-items-center justify-content-center shadow-sm"
                                                            style={{ width: '30px', height: '30px', padding: 0 }}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                sendQuoteViaWhatsApp({ quote: comp, user, sellers: dbSellers });
                                                            }}
                                                            title="Enviar por WhatsApp"
                                                        >
                                                            <i className="bi bi-whatsapp"></i>
                                                        </Button>
                                                    )}
                                                    <Button 
                                                        variant="outline-info" 
                                                        size="sm"
                                                        className="rounded-circle d-inline-flex align-items-center justify-content-center shadow-sm"
                                                        style={{ width: '30px', height: '30px', padding: 0 }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleOpenEmailModal(comp);
                                                        }}
                                                        title="Enviar por Email"
                                                    >
                                                        <i className="bi bi-envelope-fill"></i>
                                                    </Button>
                                                    <Button 
                                                        variant="outline-danger" 
                                                        size="sm"
                                                        className="rounded-circle d-inline-flex align-items-center justify-content-center shadow-sm"
                                                        style={{ width: '30px', height: '30px', padding: 0 }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            downloadQuotePDF(comp, user, dbSellers);
                                                        }}
                                                        title="Descargar PDF"
                                                    >
                                                        <i className="bi bi-file-earmark-pdf"></i>
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}

                                {comprobantes.length === 0 && (
                                    <tr>
                                        <td colSpan="9" className="text-center text-muted py-5">
                                            {isLoading ? 'Cargando presupuestos...' : 'No se encontraron comprobantes para los filtros seleccionados.'}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                </Card.Body>
                <Card.Footer className="bg-light py-2 px-3 d-flex justify-content-between align-items-center border-top text-muted small">
                    <span>Mostrando <strong>{comprobantes.length}</strong> comprobantes</span>
                    <span>Consolidado Local</span>
                </Card.Footer>
            </Card>

            {/* MODAL DE BÚSQUEDA DE CLIENTES */}
            <ClientSearchModal 
                show={showClientModal}
                onHide={() => setShowClientModal(false)}
                onSelectClient={handleSelectClient}
                clients={dbClients}
                sellers={dbSellers}
                onEditDraft={handleEditDraft}
                user={user}
            />

            {/* MODAL DE BÚSQUEDA DE VENDEDORES */}
            <SellerSearchModal 
                show={showSellerModal}
                onHide={() => setShowSellerModal(false)}
                onSelectSeller={handleSelectSeller}
                sellers={dbSellers}
                onEditDraft={handleEditDraft}
                clients={dbClients}
                presupuestos={comprobantes}
                isLoading={false}
                user={user}
            />

            {/* MODAL DE DETALLE DE PRESUPUESTO (NO EDITABLE CON TOTALES E IMPUESTOS) */}
            <QuoteDetailModal 
                show={showDetailModal}
                onHide={() => setShowDetailModal(false)}
                quote={selectedQuoteDetail}
                user={user}
                sellers={dbSellers}
                onEditDraft={handleEditDraft}
            />

            {/* MODAL DE ENVÍO DE EMAIL */}
            <SendEmailModal 
                show={showEmailModal}
                onHide={() => setShowEmailModal(false)}
                quote={selectedQuoteForEmail}
                user={user}
                sellers={dbSellers}
                onEditDraft={handleEditDraft}
            />
        </div>
    );
};

export default QuoteViewer;
